import React from "react";
import { stateHelper } from "../lib/stateHelper";
import useFXForm from "../hooks/useFXForm";
import FormError from "./FormError";
import Spinner from "./Spinner";
import FormSuccess from "./FormSuccess";
import { getCountryOptions } from "../lib/country";
import { getTranslations } from "../lib/localizedText";

const InternationalForm = ({ region, locale }) => {
  const translations = getTranslations(locale);
  const countries = getCountryOptions(locale.toLowerCase());

  const {
    formData,
    errors,
    isSubmitting,
    success,
    submitError,
    handleChange,
    handleSubmit,
  } = useFXForm(
    {
      firstName: "",
      lastName: "",
      phone: "",
      country: "",
      state: "",
      zip: "",
      flyPrivate: "",
      currentlyTravel: "",
      flyAnnually: "",
      email: "",
      aboutUs: "",
      askQuestion: "",
      keepInformed: false,
      optIn: false,
      program: "",
      gclid: "",
      utmCampaign: "",
      utmChannel: "",
      utmContent: "",
      utmMedium: "",
      utmSource: "",
      utmTerm: "",
      region: region ? "US" : "UK",
      pageSource: "",
    },
    locale
  );

  const hasStates =
    formData.country === "US" ||
    formData.country === "Canada" ||
    formData.country === "Mexico";

  // Set pageSource to locale
  formData.pageSource = locale;

  return (
    <>
      {isSubmitting ? (
        <Spinner locale={locale} />
      ) : success ? (
        <FormSuccess locale={locale} />
      ) : (
        <form className="contact-form" onSubmit={handleSubmit}>
          {submitError && <FormError locale={locale} />}

          <div className="form-group">
            <div className="form-field">
              <label>{translations.firstName}*</label>
              <input
                type="text"
                placeholder={translations.firstName}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? "input-error" : ""}
              />
              {errors.firstName && (
                <span className="error">{errors.firstName}</span>
              )}
            </div>
            <div className="form-field">
              <label>{translations.lastName}*</label>
              <input
                type="text"
                placeholder={translations.lastName}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={errors.lastName ? "input-error" : ""}
              />
              {errors.lastName && (
                <span className="error">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="form-field">
              <label>{translations.email}*</label>
              <input
                type="email"
                placeholder={translations.email}
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? "input-error" : ""}
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>
            <div className="form-field">
              <label>{translations.phone}*</label>
              <input
                type="tel"
                placeholder={translations.phone}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? "input-error" : ""}
              />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-group">
            <div className="form-field">
              <label>{translations.country}*</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={errors.country ? "input-error" : ""}
              >
                <option value="">
                  {locale === "FR"
                    ? "Sélectionnez un pays"
                    : locale === "DE"
                    ? "Wählen Sie ein Land"
                    : "Select Country"}
                </option>
                {countries.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
              {errors.country && (
                <span className="error">{errors.country}</span>
              )}
            </div>

            <div className={`form-field ${hasStates ? "" : "hidden"}`}>
              <label>{translations.state}*</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={errors.state ? "input-error" : ""}
              >
                <option value="">
                  {locale === "FR"
                    ? "Sélectionnez l'État/Province"
                    : locale === "DE"
                    ? "Wählen Sie Bundesland/Provinz"
                    : "Select State/Province"}
                </option>
                {stateHelper(formData.country)}
              </select>
              {errors.state && <span className="error">{errors.state}</span>}
            </div>
          </div>

          <div className="form-group">
            <div className="form-field">
              <label>{translations.zip}*</label>
              <input
                type="text"
                placeholder={translations.zip}
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                className={errors.zip ? "input-error" : ""}
              />
              {errors.zip && <span className="error">{errors.zip}</span>}
            </div>

            <div className="form-field">
              <label>{translations.flyPrivate}*</label>
              <select
                name="flyPrivate"
                value={formData.flyPrivate}
                onChange={handleChange}
                className={errors.flyPrivate ? "input-error" : ""}
              >
                {translations.flyPrivateOptions.map((option, index) => (
                  <option
                    key={index}
                    value={option === translations.selectOption ? "" : option}
                  >
                    {option}
                  </option>
                ))}
              </select>
              {errors.flyPrivate && (
                <span className="error">{errors.flyPrivate}</span>
              )}
            </div>
          </div>

          {(formData.flyPrivate === "Yes" ||
            formData.flyPrivate === "Oui" ||
            formData.flyPrivate === "Ja") && (
            <div className="form-group">
              <div className="form-field">
                <label>{translations.currentlyTravel}*</label>
                <select
                  name="currentlyTravel"
                  value={formData.currentlyTravel}
                  onChange={handleChange}
                  className={errors.currentlyTravel ? "input-error" : ""}
                >
                  {translations.currentlyTravelOptions.map((option, index) => (
                    <option
                      key={index}
                      value={option === translations.selectOption ? "" : option}
                    >
                      {option}
                    </option>
                  ))}
                </select>
                {errors.currentlyTravel && (
                  <span className="error">{errors.currentlyTravel}</span>
                )}
              </div>

              <div className="form-field">
                <label>{translations.flyAnnually}*</label>
                <select
                  name="flyAnnually"
                  value={formData.flyAnnually}
                  onChange={handleChange}
                  className={errors.flyAnnually ? "input-error" : ""}
                >
                  {translations.flyAnnuallyOptions.map((option, index) => (
                    <option
                      key={index}
                      value={option === translations.selectOption ? "" : option}
                    >
                      {option}
                    </option>
                  ))}
                </select>
                {errors.flyAnnually && (
                  <span className="error">{errors.flyAnnually}</span>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <div className="form-field">
              <label>{translations.program}*</label>
              <select
                name="program"
                value={formData.program}
                onChange={handleChange}
                className={errors.program ? "input-error" : ""}
              >
                {translations.programOptions.map((option, index) => (
                  <option
                    key={index}
                    value={option === translations.selectOption ? "" : option}
                  >
                    {option}
                  </option>
                ))}
              </select>
              {errors.program && (
                <span className="error">{errors.program}</span>
              )}
            </div>

            <div className="form-field">
              <label>{translations.aboutUs}*</label>
              <select
                name="aboutUs"
                value={formData.aboutUs}
                onChange={handleChange}
                className={errors.aboutUs ? "input-error" : ""}
              >
                {translations.aboutUsOptions.map((option, index) => (
                  <option
                    key={index}
                    value={option === translations.selectOption ? "" : option}
                  >
                    {option}
                  </option>
                ))}
              </select>
              {errors.aboutUs && (
                <span className="error">{errors.aboutUs}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="form-field">
              <label>{translations.askQuestion}</label>
              <textarea
                placeholder={translations.askQuestion}
                name="askQuestion"
                value={formData.askQuestion}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="checkbox-group">
            <div className="checkbox-field">
              <input
                id="keepInformed"
                type="checkbox"
                name="keepInformed"
                checked={formData.keepInformed}
                onChange={handleChange}
              />
              <label htmlFor="keepInformed">{translations.keepInformed}</label>
            </div>
            <div className="checkbox-field">
              <input
                id="optIn"
                type="checkbox"
                name="optIn"
                checked={formData.optIn}
                onChange={handleChange}
              />
              <label htmlFor="optIn">
                {translations.optIn}* <br />
                {errors.optIn && <span className="error">{errors.optIn}</span>}
              </label>
            </div>
          </div>

          <input type="hidden" value={formData.gclid} name="gclid" />
          <input
            type="hidden"
            value={formData.utmCampaign}
            name="utmCampaign"
          />
          <input type="hidden" value={formData.utmChannel} name="utmChannel" />
          <input type="hidden" value={formData.utmContent} name="utmContent" />
          <input type="hidden" value={formData.utmMedium} name="utmMedium" />
          <input type="hidden" value={formData.utmSource} name="utmSource" />
          <input type="hidden" value={formData.utmTerm} name="utmTerm" />

          <div className="submit">
            <input
              type="submit"
              value={translations.submitButton}
              className="submit-btn"
            />
          </div>
        </form>
      )}
    </>
  );
};

export default InternationalForm;
